import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Receipt, Plus, Trash2, BarChart3, Download, Filter,
  Pencil, Monitor, Smartphone, Code, Cloud, Mail, Shirt,
  Megaphone, Armchair, Wifi, GraduationCap, Users, Shield,
  Plane, Coffee, BookOpen, MoreHorizontal, TrendingUp,
  Calendar, PieChart, FolderOpen, Euro, Edit, X, Search
} from "lucide-react";
import { useOrgCurrency } from "@/lib/currency-context";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Pencil, Monitor, Smartphone, Code, Cloud, Mail, Shirt,
  Megaphone, Armchair, Wifi, GraduationCap, Users, Shield,
  Plane, Coffee, BookOpen, MoreHorizontal
};

const ZAHLUNGSARTEN = [
  { value: "ueberweisung", label: "Überweisung" },
  { value: "bar", label: "Bar" },
  { value: "kreditkarte", label: "Kreditkarte" },
  { value: "paypal", label: "PayPal" },
  { value: "lastschrift", label: "Lastschrift" },
];

/* ========== BETRIEBSAUSGABEN HAUPTKOMPONENTE ========== */
export const BetriebsausgabenTab = () => {
  const { currency, formatMoney: fmt } = useOrgCurrency();
  const [activeView, setActiveView] = useState("liste");
  const [expenses, setExpenses] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [auswertung, setAuswertung] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterKat, setFilterKat] = useState("");
  const [filterVon, setFilterVon] = useState("");
  const [filterBis, setFilterBis] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const getOrgId = () => {
    try {
      const token = localStorage.getItem("fm_admin_token");
      if (!token) return "default";
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.org_id || "default";
    } catch { return "default"; }
  };

  const loadKategorien = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/expenses/kategorien`);
      setKategorien(res.data.kategorien || []);
    } catch (err) { console.error("Kategorien laden fehlgeschlagen", err); }
  }, []);

  const loadExpenses = useCallback(async () => {
    const orgId = getOrgId();
    let params = `org_id=${orgId}`;
    if (filterKat) params += `&kategorie=${filterKat}`;
    if (filterVon) params += `&von=${filterVon}`;
    if (filterBis) params += `&bis=${filterBis}`;
    try {
      const res = await axios.get(`${API}/expenses?${params}`);
      setExpenses(res.data.expenses || []);
    } catch (err) { console.error("Ausgaben laden fehlgeschlagen", err); }
  }, [filterKat, filterVon, filterBis]);

  const loadAuswertung = useCallback(async () => {
    const orgId = getOrgId();
    try {
      const res = await axios.get(`${API}/expenses/auswertung?org_id=${orgId}`);
      setAuswertung(res.data);
    } catch (err) { console.error("Auswertung laden fehlgeschlagen", err); }
  }, []);

  const loadSummary = useCallback(async () => {
    const orgId = getOrgId();
    try {
      const res = await axios.get(`${API}/expenses/summary?org_id=${orgId}`);
      setSummary(res.data);
    } catch (err) { console.error("Summary laden fehlgeschlagen", err); }
  }, []);

  useEffect(() => { loadKategorien(); }, [loadKategorien]);
  useEffect(() => { loadExpenses(); loadSummary(); }, [loadExpenses, loadSummary]);
  useEffect(() => { if (activeView === "auswertung") loadAuswertung(); }, [activeView, loadAuswertung]);

  const getKatLabel = (id) => {
    const k = kategorien.find(x => x.id === id);
    return k ? k.label : id;
  };

  const getKatIcon = (id) => {
    const k = kategorien.find(x => x.id === id);
    if (k && ICON_MAP[k.icon]) return ICON_MAP[k.icon];
    return Receipt;
  };

  const filteredExpenses = searchTerm
    ? expenses.filter(e =>
        (e.beschreibung || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.lieferant || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.projekt || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : expenses;

  const handleExportCsv = async () => {
    const orgId = getOrgId();
    let params = `org_id=${orgId}`;
    if (filterVon) params += `&von=${filterVon}`;
    if (filterBis) params += `&bis=${filterBis}`;
    try {
      const res = await axios.get(`${API}/expenses/export/csv?${params}`);
      const rows = res.data.rows || [];
      if (rows.length === 0) { toast.error("Keine Daten zum Exportieren"); return; }
      // CSV erstellen
      const headers = Object.keys(rows[0]);
      const csvContent = [
        headers.join(";"),
        ...rows.map(r => headers.map(h => `"${String(r[h] || "").replace(/"/g, '""')}"`).join(";"))
      ].join("\n");
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Betriebsausgaben_${filterVon || "alle"}_${filterBis || "alle"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${rows.length} Einträge exportiert`);
    } catch { toast.error("Export fehlgeschlagen"); }
  };

  return (
    <div className="space-y-4" data-testid="betriebsausgaben-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold text-[var(--brand-ink)] flex items-center gap-2">
            <Receipt className="w-5 h-5" /> Betriebsausgaben
          </h2>
          <p className="text-sm text-[var(--brand-muted)]">Alle geschäftlichen Ausgaben erfassen, kategorisieren und auswerten.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={activeView === "liste" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveView("liste")}
            className={activeView === "liste" ? "bg-[var(--brand-primary)] text-white" : "border-[var(--brand-border)]"}
          >
            <FolderOpen className="w-3.5 h-3.5 mr-1" /> Ausgaben
          </Button>
          <Button
            variant={activeView === "auswertung" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveView("auswertung")}
            className={activeView === "auswertung" ? "bg-[var(--brand-primary)] text-white" : "border-[var(--brand-border)]"}
          >
            <BarChart3 className="w-3.5 h-3.5 mr-1" /> Auswertung
          </Button>
          <Button size="sm" variant="outline" onClick={handleExportCsv} className="border-[var(--brand-border)]">
            <Download className="w-3.5 h-3.5 mr-1" /> CSV-Export
          </Button>
          <Button size="sm" onClick={() => { setShowForm(true); setEditId(null); }} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
            <Plus className="w-3.5 h-3.5 mr-1" /> Neue Ausgabe
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="p-3 border-[var(--brand-border)]">
            <div className="text-xs text-[var(--brand-muted)]">Diesen Monat</div>
            <div className="text-lg font-bold text-[var(--brand-ink)]">{fmt(summary.monat_aktuell)}</div>
            <div className="text-[10px] text-[var(--brand-muted)]">{summary.anzahl_monat} Einträge</div>
          </Card>
          <Card className="p-3 border-[var(--brand-border)]">
            <div className="text-xs text-[var(--brand-muted)]">Letzter Monat</div>
            <div className="text-lg font-bold text-[var(--brand-ink)]">{fmt(summary.monat_letzter)}</div>
          </Card>
          <Card className="p-3 border-[var(--brand-border)]">
            <div className="text-xs text-[var(--brand-muted)]">Dieses Jahr</div>
            <div className="text-lg font-bold text-[var(--brand-ink)]">{fmt(summary.jahr_aktuell)}</div>
          </Card>
          <Card className="p-3 border-[var(--brand-border)]">
            <div className="text-xs text-[var(--brand-muted)]">Gesamt</div>
            <div className="text-lg font-bold text-[var(--brand-ink)]">{fmt(summary.gesamt_ausgaben)}</div>
            <div className="text-[10px] text-[var(--brand-muted)]">{summary.anzahl_gesamt} Einträge</div>
          </Card>
        </div>
      )}

      {/* Neue Ausgabe Formular */}
      {showForm && (
        <AusgabeFormular
          kategorien={kategorien}
          editId={editId}
          expenses={expenses}
          getOrgId={getOrgId}
          onClose={() => { setShowForm(false); setEditId(null); }}
          onSaved={() => { loadExpenses(); loadSummary(); loadAuswertung(); setShowForm(false); setEditId(null); }}
        />
      )}

      {/* Content */}
      {activeView === "liste" && (
        <AusgabenListe
          expenses={filteredExpenses}
          kategorien={kategorien}
          getKatLabel={getKatLabel}
          getKatIcon={getKatIcon}
          getOrgId={getOrgId}
          filterKat={filterKat}
          setFilterKat={setFilterKat}
          filterVon={filterVon}
          setFilterVon={setFilterVon}
          filterBis={filterBis}
          setFilterBis={setFilterBis}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onEdit={(id) => { setEditId(id); setShowForm(true); }}
          onDelete={async (id) => {
            if (!window.confirm("Ausgabe wirklich löschen?")) return;
            try {
              await axios.delete(`${API}/expenses/${id}?org_id=${getOrgId()}`);
              toast.success("Gelöscht");
              loadExpenses(); loadSummary();
            } catch { toast.error("Fehler beim Löschen"); }
          }}
        />
      )}

      {activeView === "auswertung" && (
        <AuswertungView auswertung={auswertung} kategorien={kategorien} getKatLabel={getKatLabel} />
      )}
    </div>
  );
};

/* ========== AUSGABE FORMULAR ========== */
const AusgabeFormular = ({ kategorien, editId, expenses, getOrgId, onClose, onSaved }) => {
  const existing = editId ? expenses.find(e => e.id === editId) : null;
  const [form, setForm] = useState({
    datum: existing?.datum || new Date().toISOString().split("T")[0],
    kategorie: existing?.kategorie || "bueromaterial",
    beschreibung: existing?.beschreibung || "",
    betrag: existing?.betrag?.toString() || "",
    mwst_satz: existing?.mwst_satz?.toString() || "19",
    zahlungsart: existing?.zahlungsart || "ueberweisung",
    beleg_nr: existing?.beleg_nr || "",
    lieferant: existing?.lieferant || "",
    projekt: existing?.projekt || "",
    kunde: existing?.kunde || "",
    notizen: existing?.notizen || "",
    wiederkehrend: existing?.wiederkehrend || false,
    intervall: existing?.intervall || "monatlich",
  });

  const handleSave = async () => {
    if (!form.beschreibung || !form.betrag) {
      toast.error("Bitte Beschreibung und Betrag angeben");
      return;
    }
    const payload = {
      ...form,
      betrag: parseFloat(form.betrag),
      mwst_satz: parseFloat(form.mwst_satz),
    };
    try {
      if (editId) {
        await axios.put(`${API}/expenses/${editId}?org_id=${getOrgId()}`, payload);
        toast.success("Ausgabe aktualisiert");
      } else {
        await axios.post(`${API}/expenses?org_id=${getOrgId()}`, payload);
        toast.success("Ausgabe erfasst");
      }
      onSaved();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const netto = parseFloat(form.betrag) || 0;
  const mwst = netto * (parseFloat(form.mwst_satz) || 0) / 100;
  const brutto = netto + mwst;

  return (
    <Card className="p-5 border-[var(--brand-border)] bg-[var(--brand-bg)]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm text-[var(--brand-ink)]">{editId ? "Ausgabe bearbeiten" : "Neue Ausgabe erfassen"}</h3>
        <Button size="icon" variant="ghost" onClick={onClose} className="h-7 w-7"><X className="w-4 h-4" /></Button>
      </div>
      <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Datum *</Label>
          <Input type="date" value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} />
        </div>
        <div>
          <Label className="text-xs">Kategorie *</Label>
          <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.kategorie} onChange={e => setForm({...form, kategorie: e.target.value})}>
            {kategorien.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-1 lg:col-span-2">
          <Label className="text-xs">Beschreibung *</Label>
          <Input placeholder="z.B. Druckerpatronen HP 305" value={form.beschreibung} onChange={e => setForm({...form, beschreibung: e.target.value})} />
        </div>
        <div>
          <Label className="text-xs">Netto-Betrag ({currency}) *</Label>
          <Input type="number" step="0.01" placeholder="z.B. 49.90" value={form.betrag} onChange={e => setForm({...form, betrag: e.target.value})} />
        </div>
        <div>
          <Label className="text-xs">MwSt-Satz</Label>
          <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.mwst_satz} onChange={e => setForm({...form, mwst_satz: e.target.value})}>
            <option value="19">19%</option>
            <option value="7">7%</option>
            <option value="0">0% (steuerfrei)</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Zahlungsart</Label>
          <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.zahlungsart} onChange={e => setForm({...form, zahlungsart: e.target.value})}>
            {ZAHLUNGSARTEN.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Beleg-Nr.</Label>
          <Input placeholder="z.B. RE-2026-0042" value={form.beleg_nr} onChange={e => setForm({...form, beleg_nr: e.target.value})} />
        </div>
        <div>
          <Label className="text-xs">Lieferant</Label>
          <Input placeholder="z.B. Amazon, MediaMarkt" value={form.lieferant} onChange={e => setForm({...form, lieferant: e.target.value})} />
        </div>
        <div>
          <Label className="text-xs">Projekt</Label>
          <Input placeholder="z.B. Website-Relaunch" value={form.projekt} onChange={e => setForm({...form, projekt: e.target.value})} />
        </div>
        <div>
          <Label className="text-xs">Kunde</Label>
          <Input placeholder="z.B. Firma Müller GmbH" value={form.kunde} onChange={e => setForm({...form, kunde: e.target.value})} />
        </div>
        <div>
          <Label className="text-xs">Wiederkehrend?</Label>
          <div className="flex items-center gap-2 h-10">
            <input type="checkbox" checked={form.wiederkehrend} onChange={e => setForm({...form, wiederkehrend: e.target.checked})} className="w-4 h-4" />
            {form.wiederkehrend && (
              <select className="h-8 rounded-md border border-[var(--brand-border)] bg-white px-2 text-xs" value={form.intervall} onChange={e => setForm({...form, intervall: e.target.value})}>
                <option value="monatlich">Monatlich</option>
                <option value="quartalsweise">Quartalsweise</option>
                <option value="jaehrlich">Jährlich</option>
              </select>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-xs">Notizen</Label>
        <Textarea placeholder="Optionale Notizen..." value={form.notizen} onChange={e => setForm({...form, notizen: e.target.value})} rows={2} />
      </div>
      {form.betrag && (
        <div className="mt-2 text-xs text-[var(--brand-muted)] flex gap-4">
          <span>Netto: <strong>{fmt(netto)}</strong></span>
          <span>MwSt ({form.mwst_satz}%): <strong>{fmt(mwst)}</strong></span>
          <span>Brutto: <strong className="text-[var(--brand-ink)]">{fmt(brutto)}</strong></span>
        </div>
      )}
      <div className="flex gap-2 mt-3">
        <Button size="sm" onClick={handleSave} className="bg-[var(--brand-ink)] text-white">{editId ? "Aktualisieren" : "Speichern"}</Button>
        <Button size="sm" variant="outline" onClick={onClose}>Abbrechen</Button>
      </div>
    </Card>
  );
};

/* ========== AUSGABEN LISTE ========== */
const AusgabenListe = ({ expenses, kategorien, getKatLabel, getKatIcon, getOrgId, filterKat, setFilterKat, filterVon, setFilterVon, filterBis, setFilterBis, searchTerm, setSearchTerm, onEdit, onDelete }) => {
  return (
    <div className="space-y-3">
      {/* Filter */}
      <Card className="p-3 border-[var(--brand-border)]">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-[10px] text-[var(--brand-muted)]">Suche</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-[var(--brand-muted)]" />
              <Input className="pl-8 h-9" placeholder="Beschreibung, Lieferant, Projekt..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>
          <div className="w-40">
            <Label className="text-[10px] text-[var(--brand-muted)]">Kategorie</Label>
            <select className="w-full h-9 rounded-md border border-[var(--brand-border)] bg-white px-2 text-sm" value={filterKat} onChange={e => setFilterKat(e.target.value)}>
              <option value="">Alle</option>
              {kategorien.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </div>
          <div className="w-36">
            <Label className="text-[10px] text-[var(--brand-muted)]">Von</Label>
            <Input type="date" className="h-9" value={filterVon} onChange={e => setFilterVon(e.target.value)} />
          </div>
          <div className="w-36">
            <Label className="text-[10px] text-[var(--brand-muted)]">Bis</Label>
            <Input type="date" className="h-9" value={filterBis} onChange={e => setFilterBis(e.target.value)} />
          </div>
          {(filterKat || filterVon || filterBis || searchTerm) && (
            <Button size="sm" variant="ghost" className="text-xs text-[var(--brand-muted)]" onClick={() => { setFilterKat(""); setFilterVon(""); setFilterBis(""); setSearchTerm(""); }}>
              <X className="w-3 h-3 mr-1" /> Filter zurücksetzen
            </Button>
          )}
        </div>
      </Card>

      {/* Liste */}
      {expenses.length === 0 ? (
        <div className="text-center py-10 text-[var(--brand-muted)]">
          <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Noch keine Betriebsausgaben erfasst.</p>
          <p className="text-xs mt-1">Erfasse deine erste Ausgabe über den Button oben.</p>
        </div>
      ) : (
        <Card className="border-[var(--brand-border)] overflow-hidden">
          <div className="divide-y divide-[var(--brand-border-light)]">
            {expenses.slice(0, 50).map(e => {
              const KatIcon = getKatIcon(e.kategorie);
              return (
                <div key={e.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--brand-bg)] transition">
                  <div className="w-8 h-8 rounded-full bg-[var(--brand-primary)]/10 flex items-center justify-center shrink-0">
                    <KatIcon className="w-4 h-4 text-[var(--brand-primary)]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-[var(--brand-ink)] truncate">{e.beschreibung}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--brand-bg)] text-[var(--brand-muted)]">{getKatLabel(e.kategorie)}</span>
                      {e.wiederkehrend && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">↻ {e.intervall}</span>}
                    </div>
                    <div className="text-[11px] text-[var(--brand-muted)] mt-0.5">
                      {[e.datum, e.lieferant, e.projekt && `Projekt: ${e.projekt}`, e.beleg_nr && `#${e.beleg_nr}`].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-sm text-[var(--brand-ink)]">{fmt(e.betrag)}</div>
                    <div className="text-[10px] text-[var(--brand-muted)]">brutto {fmt(e.brutto)}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[var(--brand-muted)]" onClick={() => onEdit(e.id)}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => onDelete(e.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          {expenses.length > 50 && (
            <div className="text-center py-2 text-xs text-[var(--brand-muted)] border-t border-[var(--brand-border-light)]">
              Zeige 50 von {expenses.length} Einträgen
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

/* ========== AUSWERTUNG VIEW ========== */
const AuswertungView = ({ auswertung, kategorien, getKatLabel }) => {
  if (!auswertung) {
    return (
      <div className="text-center py-8 text-[var(--brand-muted)]">
        <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p className="text-sm">Auswertung wird geladen...</p>
      </div>
    );
  }

  if (auswertung.anzahl === 0) {
    return (
      <div className="text-center py-8 text-[var(--brand-muted)]">
        <PieChart className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p className="text-sm">Noch keine Daten für Auswertungen vorhanden.</p>
      </div>
    );
  }

  const sortedKategorien = Object.entries(auswertung.nach_kategorie || {})
    .sort((a, b) => b[1].netto - a[1].netto);

  const sortedMonate = Object.entries(auswertung.nach_monat || {})
    .sort((a, b) => a[0].localeCompare(b[0]));

  const sortedProjekte = Object.entries(auswertung.nach_projekt || {})
    .filter(([k]) => k !== "Ohne Projekt")
    .sort((a, b) => b[1].netto - a[1].netto);

  const sortedKunden = Object.entries(auswertung.nach_kunde || {})
    .filter(([k]) => k !== "Ohne Kunde")
    .sort((a, b) => b[1].netto - a[1].netto);

  return (
    <div className="space-y-4">
      {/* Gesamtübersicht */}
      <Card className="p-4 border-[var(--brand-border)]">
        <h3 className="font-semibold text-sm text-[var(--brand-ink)] mb-3 flex items-center gap-2">
          <Euro className="w-4 h-4" /> Gesamtübersicht
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg p-3 bg-[var(--brand-bg)]">
            <div className="text-xs text-[var(--brand-muted)]">Netto gesamt</div>
            <div className="text-xl font-bold text-[var(--brand-ink)]">{fmt(auswertung.gesamt_netto)}</div>
          </div>
          <div className="rounded-lg p-3 bg-[var(--brand-bg)]">
            <div className="text-xs text-[var(--brand-muted)]">MwSt gesamt</div>
            <div className="text-xl font-bold text-[var(--brand-ink)]">{fmt(auswertung.gesamt_mwst)}</div>
          </div>
          <div className="rounded-lg p-3 bg-[var(--brand-bg)]">
            <div className="text-xs text-[var(--brand-muted)]">Brutto gesamt</div>
            <div className="text-xl font-bold text-[var(--brand-ink)]">{fmt(auswertung.gesamt_brutto)}</div>
          </div>
        </div>
      </Card>

      {/* Nach Kategorie */}
      <Card className="p-4 border-[var(--brand-border)]">
        <h3 className="font-semibold text-sm text-[var(--brand-ink)] mb-3 flex items-center gap-2">
          <PieChart className="w-4 h-4" /> Kosten nach Kategorie
        </h3>
        <div className="space-y-2">
          {sortedKategorien.map(([kat, data]) => {
            const prozent = auswertung.gesamt_netto > 0 ? (data.netto / auswertung.gesamt_netto * 100) : 0;
            return (
              <div key={kat} className="flex items-center gap-3">
                <span className="text-xs w-40 truncate text-[var(--brand-muted)]">{getKatLabel(kat)}</span>
                <div className="flex-1 h-5 bg-[var(--brand-border-light)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[var(--brand-primary)] rounded-full transition-all"
                    style={{ width: `${Math.min(prozent, 100)}%` }}
                  />
                </div>
                <span className="text-xs font-medium w-20 text-right">{fmt(data.netto)}</span>
                <span className="text-[10px] text-[var(--brand-muted)] w-12 text-right">{prozent.toFixed(1)}%</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Nach Monat */}
      {sortedMonate.length > 0 && (
        <Card className="p-4 border-[var(--brand-border)]">
          <h3 className="font-semibold text-sm text-[var(--brand-ink)] mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Monatsübersicht
          </h3>
          <div className="space-y-1.5">
            {sortedMonate.map(([monat, data]) => {
              const maxNetto = Math.max(...sortedMonate.map(([, d]) => d.netto));
              const prozent = maxNetto > 0 ? (data.netto / maxNetto * 100) : 0;
              return (
                <div key={monat} className="flex items-center gap-3">
                  <span className="text-xs w-20 text-[var(--brand-muted)]">{monat}</span>
                  <div className="flex-1 h-4 bg-[var(--brand-border-light)] rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${prozent}%` }} />
                  </div>
                  <span className="text-xs font-medium w-20 text-right">{fmt(data.netto)}</span>
                  <span className="text-[10px] text-[var(--brand-muted)] w-8 text-right">{data.anzahl}×</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Nach Projekt */}
      {sortedProjekte.length > 0 && (
        <Card className="p-4 border-[var(--brand-border)]">
          <h3 className="font-semibold text-sm text-[var(--brand-ink)] mb-3 flex items-center gap-2">
            <FolderOpen className="w-4 h-4" /> Kosten nach Projekt
          </h3>
          <div className="space-y-1.5">
            {sortedProjekte.map(([projekt, data]) => (
              <div key={projekt} className="flex items-center justify-between rounded-lg border border-[var(--brand-border-light)] px-3 py-2">
                <span className="text-sm text-[var(--brand-ink)]">{projekt}</span>
                <div className="text-right">
                  <span className="font-medium text-sm">{fmt(data.netto)}</span>
                  <span className="text-[10px] text-[var(--brand-muted)] ml-2">{data.anzahl} Einträge</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Nach Kunde */}
      {sortedKunden.length > 0 && (
        <Card className="p-4 border-[var(--brand-border)]">
          <h3 className="font-semibold text-sm text-[var(--brand-ink)] mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" /> Kosten nach Kunde
          </h3>
          <div className="space-y-1.5">
            {sortedKunden.map(([kunde, data]) => (
              <div key={kunde} className="flex items-center justify-between rounded-lg border border-[var(--brand-border-light)] px-3 py-2">
                <span className="text-sm text-[var(--brand-ink)]">{kunde}</span>
                <div className="text-right">
                  <span className="font-medium text-sm">{fmt(data.netto)}</span>
                  <span className="text-[10px] text-[var(--brand-muted)] ml-2">{data.anzahl} Einträge</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Budget-Vergleich */}
      {auswertung.budget_vergleich && auswertung.budget_vergleich.length > 0 && (
        <Card className="p-4 border-[var(--brand-border)]">
          <h3 className="font-semibold text-sm text-[var(--brand-ink)] mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Budgetübersicht
          </h3>
          <div className="space-y-2">
            {auswertung.budget_vergleich.map(b => (
              <div key={b.kategorie} className="flex items-center gap-3">
                <span className="text-xs w-36 truncate text-[var(--brand-muted)]">{getKatLabel(b.kategorie)}</span>
                <div className="flex-1 h-5 bg-[var(--brand-border-light)] rounded-full overflow-hidden relative">
                  <div
                    className={`h-full rounded-full ${b.prozent > 100 ? "bg-red-500" : b.prozent > 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${Math.min(b.prozent, 100)}%` }}
                  />
                </div>
                <span className="text-xs w-20 text-right">{fmt(b.ausgegeben)}</span>
                <span className="text-[10px] text-[var(--brand-muted)] w-20 text-right">/ {fmt(b.budget_jaehrlich)}</span>
                <span className={`text-[10px] font-medium w-12 text-right ${b.prozent > 100 ? "text-red-500" : "text-emerald-600"}`}>{b.prozent}%</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default BetriebsausgabenTab;
